/**
 * Ideas Kitchen API Client V2
 *
 * ARCHITECTURE:
 * - MongoDB-backed persistence
 * - Async file processing (returns 202)
 * - Polling for source status
 * - Truthful badges based on real provider
 */

import axios from 'axios';

import { getAuthToken as getCanonicalAuthToken } from '../../../utils/auth';
import { refreshToken as refreshAccessToken } from '../../../utils/auth';
import { getApiBaseUrl } from '../../../config/runtimeUrls';

const UPLOAD_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes (large files + slow networks)
const TENANT_ID_REGEX = /^[0-9a-fA-F]{24}$/;
const RESERVED_SUBDOMAINS = new Set(['www', 'app', 'admin', 'api', 'login']);

// ✅ Enterprise: Single token getter (Auth v2 + legacy fallbacks)
const getAuthToken = () => {
  try {
    const raw = getCanonicalAuthToken() || '';
    const t = String(raw || '').trim();
    const lower = t.toLowerCase();
    const isJwtLike = t.split('.').length >= 3;
    if (!t || lower === 'null' || lower === 'undefined' || !isJwtLike) return '';
    return t;
  } catch {
    return '';
  }
};

const isPlainObject = value =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const parseJsonSafe = value => {
  try {
    return JSON.parse(String(value || '{}'));
  } catch {
    return null;
  }
};

const normalizeSlug = value =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '');

const getTenantIdFromStorage = () => {
  if (typeof window === 'undefined') return '';
  const directTenantId = String(window.localStorage.getItem('tenantId') || '').trim();
  if (TENANT_ID_REGEX.test(directTenantId)) return directTenantId;

  const authData = parseJsonSafe(window.localStorage.getItem('authData'));
  const userData = parseJsonSafe(window.localStorage.getItem('user'));
  const nestedTenantId = String(authData?.tenantId || userData?.tenantId || '').trim();
  if (TENANT_ID_REGEX.test(nestedTenantId)) return nestedTenantId;
  return '';
};

const getTenantSlugFromStorageOrHost = () => {
  if (typeof window === 'undefined') return '';
  const fromStorage = normalizeSlug(
    window.localStorage.getItem('tenantSlug') ||
      parseJsonSafe(window.localStorage.getItem('authData'))?.tenantSlug ||
      parseJsonSafe(window.localStorage.getItem('user'))?.tenantSlug ||
      ''
  );
  if (fromStorage) return fromStorage;

  const host = String(window.location.hostname || '').trim().toLowerCase();
  if (!host || host === 'localhost' || host === '127.0.0.1') return '';
  const hostParts = host.split('.').filter(Boolean);
  if (hostParts.length < 3) return '';

  const candidate = normalizeSlug(hostParts[0]);
  if (!candidate || RESERVED_SUBDOMAINS.has(candidate)) return '';
  return candidate;
};

/**
 * Backend uses a normalized envelope: { success, data, error }.
 * Flatten object payloads so IK callers can read legacy fields directly
 * (e.g. response.answer, response.sources) without losing success/error metadata.
 */
const normalizeApiEnvelope = payload => {
  if (!isPlainObject(payload)) return payload;

  const hasEnvelope =
    Object.prototype.hasOwnProperty.call(payload, 'success') &&
    (Object.prototype.hasOwnProperty.call(payload, 'data') ||
      Object.prototype.hasOwnProperty.call(payload, 'error'));

  if (!hasEnvelope) return payload;

  const success = Boolean(payload.success);
  const error =
    payload.error && isPlainObject(payload.error)
      ? payload.error
      : typeof payload.error === 'string'
        ? { code: 'REQUEST_FAILED', message: payload.error }
        : null;
  const envelopeMessage =
    payload.message || (error && typeof error.message === 'string' ? error.message : null);
  const data = Object.prototype.hasOwnProperty.call(payload, 'data') ? payload.data : null;

  if (isPlainObject(data)) {
    return {
      ...data,
      success,
      error,
      message: data.message || envelopeMessage || null,
      _envelope: payload,
    };
  }

  return {
    success,
    error,
    message: envelopeMessage || null,
    data: data ?? null,
    _envelope: payload,
  };
};

let apiInstance = null;

const createApi = () => {
  const baseUrl = String(getApiBaseUrl() || '').replace(/\/+$/, '');
  const ideasKitchenBase = `${baseUrl}/ideas-kitchen`;
  console.log('[IK API] Creating axios instance with base:', ideasKitchenBase);

  const instance = axios.create({
    baseURL: ideasKitchenBase,
    timeout: 30000, // 30s max - async processing means no long waits
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true, // allow cookie-based auth as fallback
  });

  // Add auth token
  instance.interceptors.request.use(config => {
    config.headers = config.headers || {};
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const tenantId = getTenantIdFromStorage();
    const tenantSlug = getTenantSlugFromStorageOrHost();
    if (tenantId) {
      config.headers['X-Tenant-ID'] = tenantId;
      config.headers['X-Organization-ID'] = tenantId;
    }
    if (tenantSlug) {
      config.headers['X-Tenant-Slug'] = tenantSlug;
    }

    return config;
  });

  // Handle responses
  instance.interceptors.response.use(
    response => normalizeApiEnvelope(response.data),
    async error => {
      const originalRequest = error?.config || {};
      const status = Number(error?.response?.status || 0);
      if (
        status === 401 &&
        !originalRequest._retry &&
        !String(originalRequest?.url || '').includes('/auth/refresh-token')
      ) {
        originalRequest._retry = true;
        try {
          const refreshedToken = await refreshAccessToken();
          const token = String(getAuthToken() || refreshedToken || '').trim();
          if (token) {
            originalRequest.headers = {
              ...(originalRequest.headers || {}),
              Authorization: `Bearer ${token}`,
            };
            const retryResponse = await instance(originalRequest);
            return retryResponse;
          }
        } catch (_) {
          // Fall through to normalized auth error below.
        }
      }

      console.error('[Ideas Kitchen API] Error:', error.response?.data || error.message);
      const data = normalizeApiEnvelope(error.response?.data);
      // Normalize common auth failure shape
      if (error.response?.status === 401) {
        throw {
          success: false,
          error: data?.error || data?.code || 'AUTH_REQUIRED',
          message: data?.message || 'Authentication required',
        };
      }
      throw data || {
        success: false,
        error: { code: 'REQUEST_FAILED', message: error?.message || 'Request failed' },
        message: error?.message || 'Request failed',
      };
    }
  );

  return instance;
};

const getApi = () => {
  if (!apiInstance) {
    apiInstance = createApi();
  }
  return apiInstance;
};

const api = {
  get: (...args) => getApi().get(...args),
  post: (...args) => getApi().post(...args),
  put: (...args) => getApi().put(...args),
  patch: (...args) => getApi().patch(...args),
  delete: (...args) => getApi().delete(...args),
};

// ============================================================
// NOTEBOOK OPERATIONS
// ============================================================

export const listNotebooks = async () => {
  return api.get('/notebooks');
};

export const createNotebook = async ({ name, description }) => {
  return api.post('/notebooks', { name, description });
};

export const getNotebook = async notebookId => {
  return api.get(`/notebooks/${notebookId}`);
};

export const deleteNotebook = async notebookId => {
  return api.delete(`/notebooks/${notebookId}`);
};

// ============================================================
// FILE UPLOAD (ASYNC - Returns 202)
// ============================================================

/**
 * Upload files to a notebook
 * Returns 202 Accepted - use pollSourcesStatus for updates
 */
export const uploadFiles = async (notebookId, files, options = {}) => {
  const formData = new FormData();

  if (Array.isArray(files)) {
    files.forEach(file => formData.append('files', file));
  } else {
    formData.append('files', files);
  }

  const timeoutMs =
    Number.isFinite(Number(options.timeoutMs)) && Number(options.timeoutMs) > 0
      ? Number(options.timeoutMs)
      : UPLOAD_TIMEOUT_MS;

  return api.post(`/notebooks/${notebookId}/files`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: timeoutMs,
    onUploadProgress:
      typeof options.onUploadProgress === 'function' ? options.onUploadProgress : undefined,
    signal: options.signal, // optional AbortController support
  });
};

// Legacy alias
export const uploadFile = uploadFiles;

/**
 * Poll sources status (for progress tracking)
 */
export const getSourcesStatus = async notebookId => {
  return api.get(`/notebooks/${notebookId}/sources/status`);
};

/**
 * Get file preview (extracted text)
 */
export const getFilePreview = async (notebookId, fileId) => {
  return api.get(`/notebooks/${notebookId}/files/${fileId}/preview`);
};

/**
 * Retry a failed source
 */
export const retrySource = async (notebookId, fileId) => {
  return api.post(`/notebooks/${notebookId}/files/${fileId}/retry`);
};

/**
 * Get Ideas Kitchen quota status
 */
export const getQuotaStatus = async notebookId => {
  return api.get(`/notebooks/${notebookId}/quotas`);
};

/**
 * Delete a file from a notebook
 */
export const deleteFile = async (notebookId, fileId) => {
  return api.delete(`/notebooks/${notebookId}/files/${fileId}`);
};

/**
 * Poll sources until all are ready or failed
 * @param {string} notebookId
 * @param {function} onUpdate - Called with status updates
 * @param {number} maxAttempts - Max poll attempts (default 60 = 2 mins)
 */
export const pollUntilReady = async (notebookId, onUpdate, maxAttempts = 60) => {
  let attempts = 0;
  const POLL_INTERVAL_MS = 3000;
  const POLL_BACKOFF_ON_429 = 10000;
  let intervalMs = POLL_INTERVAL_MS;

  const poll = async () => {
    if (attempts >= maxAttempts) {
      throw new Error('Polling timeout - processing is taking too long');
    }

    let result = null;
    try {
      result = await getSourcesStatus(notebookId);
      intervalMs = POLL_INTERVAL_MS;
    } catch (err) {
      const statusCode = Number(err?.status || err?.statusCode || err?.response?.status || 0);
      const isRateLimited =
        statusCode === 429 ||
        err?.code === 'RATE_LIMIT_EXCEEDED' ||
        err?.error?.code === 'RATE_LIMIT_EXCEEDED';
      if (isRateLimited) {
        intervalMs = POLL_BACKOFF_ON_429;
      } else {
        throw err;
      }
    }

    if (result && onUpdate) {
      onUpdate(result);
    }

    if (result) {
      // Check if all sources are done (ready or failed)
      const allDone = result.sources?.every(s => s.status === 'ready' || s.status === 'failed');

      if (allDone || result.notebookStatus === 'ready') {
        return result;
      }
    }

    attempts++;
    await new Promise(r => setTimeout(r, intervalMs));
    return poll();
  };

  return poll();
};

// ============================================================
// CHAT (Persisted to MongoDB)
// ============================================================

/**
 * Send a chat message
 */
export const sendMessage = async (notebookId, message) => {
  return api.post(`/notebooks/${notebookId}/chat`, { message });
};

/**
 * Get chat history
 */
export const getChatHistory = async notebookId => {
  return api.get(`/notebooks/${notebookId}/chat`);
};

/**
 * Clear chat history for a notebook
 */
export const clearChatHistory = async notebookId => {
  return api.delete(`/notebooks/${notebookId}/chat`);
};

/**
 * Export notebook (DSAR)
 */
export const exportNotebook = async notebookId => {
  return api.get(`/notebooks/${notebookId}/export`);
};

/**
 * Set legal hold on notebook
 */
export const setLegalHold = async (notebookId, enabled = true) => {
  return api.post(`/notebooks/${notebookId}/legal-hold`, { enabled });
};

// Legacy alias
export const askQuestion = async (notebookId, message, options = {}) => {
  return api.post(
    `/notebooks/${notebookId}/ask`,
    {
      message,
      question: message,
      ...options,
    },
    {
      // ✅ Ask can take longer (Gemini + optional rewrite pass). Do NOT fail at 30s.
      timeout: 120000,
    }
  );
};

// ============================================================
// SUMMARIZATION
// ============================================================

export const generateSummary = async (notebookId, options = {}) => {
  const response = await api.post(`/notebooks/${notebookId}/summarize`, {
    type: options.type || 'corpus',
    length: options.length || 'medium',
  });

  // ✅ Normalize response shapes for SummaryPanel
  if (response.success && response.answer) {
    return {
      ...response,
      summary: {
        text: response.answer.text || '',
        sourcesUsed: response.answer.sourcesUsed || [],
        confidence: response.answer.confidence || 'medium',
        citations: response.answer.citations || [],
      },
    };
  }

  // Backend may return summary as a string (legacy) or as an object (enterprise)
  if (response.success && typeof response.summary === 'string') {
    return {
      ...response,
      summary: {
        text: response.summary || '',
        sourcesUsed: [],
        confidence: 'medium',
        citations: [],
      },
    };
  }

  return response;
};

// Legacy alias
export const generateCorpusSummary = generateSummary;

// Enterprise: Summarize a single source (implemented via /ask structured SUMMARY)
export const generateSourceSummary = async (notebookId, sourceId, options = {}) => {
  const message = `Summarize source ${sourceId}. Provide an executive summary and key points.`;
  const response = await askQuestion(notebookId, message, {
    taskType: 'SUMMARY',
    topK: options.topK || 8,
  });

  return {
    success: true,
    summary: {
      text: response?.answer?.structured?.summary || response?.answer?.text || '',
      keyPoints: response?.answer?.structured?.keyPoints || [],
      sourcesUsed: response?.answer?.sourcesUsed || [],
      confidence: response?.answer?.confidence || 'medium',
    },
    traceId: response?.traceId,
  };
};

// Enterprise: Topic-focused summary (implemented via /ask structured SUMMARY)
export const generateTopicSummary = async (notebookId, topic, options = {}) => {
  const message = `Provide a summary focused on: ${topic}. Include key points relevant to this topic.`;
  const response = await askQuestion(notebookId, message, {
    taskType: 'SUMMARY',
    topK: options.topK || 10,
  });

  return {
    success: true,
    summary: {
      text: response?.answer?.structured?.summary || response?.answer?.text || '',
      keyPoints: response?.answer?.structured?.keyPoints || [],
      sourcesUsed: response?.answer?.sourcesUsed || [],
      confidence: response?.answer?.confidence || 'medium',
    },
    traceId: response?.traceId,
  };
};

// ============================================================
// OUTPUTS (Persisted summaries/audio/etc.)
// ============================================================

export const listOutputs = async (notebookId, type = null) => {
  const params = type ? { type } : undefined;
  return api.get(`/notebooks/${notebookId}/outputs`, { params });
};

export const saveOutput = async (notebookId, payload) => {
  return api.post(`/notebooks/${notebookId}/outputs`, payload);
};

// ============================================================
// AUDIO OVERVIEWS
// ============================================================

export const listAudioOverviews = async notebookId => {
  return api.get(`/notebooks/${notebookId}/audio`);
};

export const generateAudioOverview = async (notebookId, payload = {}) => {
  return api.post(`/notebooks/${notebookId}/audio`, payload);
};

export const getAudioStatus = async (notebookId, jobId) => {
  return api.get(`/notebooks/${notebookId}/audio/${jobId}`);
};

// ============================================================
// SERVICE STATUS
// ============================================================

export const getStatus = async () => {
  return api.get('/status');
};

// ============================================================
// PROCESSING STATUS HELPERS
// ============================================================

/**
 * Get badge info for a source based on its status and provider
 */
export const getSourceBadgeInfo = source => {
  const { status, extractionProvider, ocrProvider, errorCode } = source;

  // Failed states
  if (status === 'failed') {
    return {
      type: 'error',
      label: errorCode || 'Failed',
      color: '#ef4444',
      icon: '❌',
    };
  }

  // Processing states
  if (['pending', 'uploading', 'ocr_queued', 'ocr_processing', 'extracting'].includes(status)) {
    return {
      type: 'processing',
      label: 'Processing...',
      color: '#f59e0b',
      icon: '⏳',
    };
  }

  // Ready - determine provider badge
  if (status === 'ready') {
    // Check if cloud processing was used
    const isCloudOCR = ['google_vision', 'openai_vision'].includes(ocrProvider);
    const isCloudExtraction = extractionProvider === 'gemini';

    if (isCloudOCR || isCloudExtraction) {
      return {
        type: 'cloud',
        label:
          ocrProvider === 'google_vision'
            ? 'Google Vision'
            : ocrProvider === 'openai_vision'
              ? 'OpenAI Vision'
              : 'Cloud Processed',
        color: '#10b981',
        icon: '☁️',
      };
    }

    // Local processing
    return {
      type: 'local',
      label: 'Processed',
      color: '#6b7280',
      icon: '📄',
    };
  }

  // Unknown
  return {
    type: 'unknown',
    label: status || 'Unknown',
    color: '#9ca3af',
    icon: '❓',
  };
};

// ============================================================
// DEFAULT EXPORT
// ============================================================

export default {
  // Notebooks
  listNotebooks,
  createNotebook,
  getNotebook,
  deleteNotebook,
  // Files
  uploadFile,
  uploadFiles,
  getSourcesStatus,
  getFilePreview,
  retrySource,
  getQuotaStatus,
  deleteFile,
  pollUntilReady,
  // Chat
  sendMessage,
  getChatHistory,
  clearChatHistory,
  exportNotebook,
  setLegalHold,
  askQuestion,
  // Summary
  generateSummary,
  generateCorpusSummary,
  generateSourceSummary,
  generateTopicSummary,
  // Outputs
  listOutputs,
  saveOutput,
  // Status
  getStatus,
  // Audio
  listAudioOverviews,
  generateAudioOverview,
  getAudioStatus,
  // Helpers
  getSourceBadgeInfo,
};
